<template>
  <div class="invoice-print-wrapper p-2 p-md-4 bg-white min-vh-100 text-dark">
    <!-- Print action buttons -->
    <div class="d-flex justify-content-end align-items-center mb-2 print-hide">
      <div>
        <b-button variant="outline-secondary" class="mr-1" @click="handleCancel">
          Back
        </b-button>
        <b-button variant="primary" @click="handlePrint">
          <feather-icon icon="PrinterIcon" class="mr-50" />
          Print Invoice
        </b-button>
      </div>
    </div>

    <div v-if="isLoading" class="text-center py-5 print-hide">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Loading...</span>
      </div>
    </div>

    <!-- Main Print Area -->
    <div v-else>
      <div v-for="(inv, idx) in invoices" :key="idx" class="print-area invoice-print-content mx-auto border border-dark rounded page-break">
        
        <!-- Top header -->
        <div class="text-center invoice-print-header">
          <h2 class="font-weight-bolder text-capitalize mb-0">{{ businessData ? businessData.name : '-' }}</h2>
          <p class="mb-0">ADDRESS: {{ businessData ? businessData.address : '-' }}</p>
          <p class="mb-0">{{ getBusinessNtnCnicLabel(businessData) }}: {{ getBusinessNtnCnicValue(businessData) }}</p>
          <p v-if="getBusinessStrn(businessData)" class="mb-0">STRN: {{ getBusinessStrn(businessData) }}</p>
        </div>

        <h3 class="text-center font-weight-bolder text-uppercase invoice-print-title">SALES TAX INVOICE</h3>

        <!-- Customer & Invoice Info blocks -->
        <div class="invoice-info-row">
          <div class="invoice-info-box invoice-info-box-customer p-2 border border-dark">
            <table class="w-100 invoice-info-table" style="font-size: 15px;">
              <tr>
                <td class="invoice-info-label align-top pb-1">CUSTOMER:</td>
                <td class="text-uppercase text-right pb-1 invoice-info-value">{{ inv.customerData.name || inv.invoiceData.metaCustomerName }}</td>
              </tr>
              <tr>
                <td class="invoice-info-label align-top pb-1">CNIC / NTN NO.:</td>
                <td class="text-uppercase text-right pb-1 invoice-info-value">{{ inv.customerData.ntnNumber || inv.customerData.cnicNumber || '-' }}</td>
              </tr>
              <tr v-if="getCustomerGstNo(inv.customerData)">
                <td class="invoice-info-label align-top pb-1">STRN:</td>
                <td class="text-uppercase text-right pb-1 invoice-info-value">{{ getCustomerGstNo(inv.customerData) }}</td>
              </tr>
              <tr>
                <td colspan="2" class="align-top text-uppercase invoice-info-value">
                  <span class="invoice-info-label">ADDRESS: </span>{{ inv.customerData.address || '-' }}
                </td>
              </tr>
            </table>
          </div>
          
          <div class="invoice-info-box invoice-info-box-meta p-2 border border-dark">
            <table class="w-100 invoice-info-table invoice-meta-table">
              <tr>
                <td class="invoice-info-label align-top pb-2">DIGITAL INVOICE NO:</td>
                <td class="text-right align-top pb-2 invoice-info-value invoice-meta-value">SI-{{ inv.invoiceData.salesTaxInvoiceNo || ''}}</td>
              </tr>
              <tr>
                <td class="invoice-info-label align-top">DATE:</td>
                <td class="text-right align-top invoice-info-value invoice-meta-value">{{ formatDate(inv.invoiceData.tranDate) }}</td>
              </tr>
            </table>
          </div>
        </div>

        <!-- Items Table -->
        <table class="table table-bordered table-sm text-center border-dark print-table font-weight-bold">
          <thead class="text-dark font-weight-bolder" style="border: 1px solid black !important;">
            <tr>
              <th style="border: 1px solid black !important; font-weight: 900;" class="align-middle">S.NO</th>
              <th style="border: 1px solid black !important; font-weight: 900;" class="align-middle">HS<br>CODE</th>
              <th style="border: 1px solid black !important; font-weight: 900;" class="align-middle text-center">DESCRIPTION<br>OF GOOD'S</th>
              <th style="border: 1px solid black !important; font-weight: 900;" class="align-middle">UOM</th>
              <th style="border: 1px solid black !important; font-weight: 900;" class="align-middle">QTY</th>
              <th style="border: 1px solid black !important; font-weight: 900;" class="align-middle">Unit<br>Price</th>
              <th style="border: 1px solid black !important; font-weight: 900;" class="align-middle">VALUE<br>EXC.<br>SALES<br>TAX</th>
              <th style="border: 1px solid black !important; font-weight: 900;" class="align-middle">SALES<br>TAX %</th>
              <th style="border: 1px solid black !important; font-weight: 900;" class="align-middle">AMOUNT<br>OF SALE<br>TAX</th>
              <th style="border: 1px solid black !important; font-weight: 900;" class="align-middle">VALUE<br>INCLUDING<br>SALES TAX</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in inv.invoiceDetails" :key="index" class="font-weight-bold">
              <td style="border: 1px solid black !important;" class="align-middle font-weight-bold">{{ index + 1 }}</td>
              <td style="border: 1px solid black !important;" class="align-middle font-weight-bold">{{ item.itemHSCode || item.metaHSCode || '-' }}</td>
              <td style="border: 1px solid black !important;" class="text-left font-weight-bold align-middle">{{ item.metaItemName || 'Item ' + item.itemId }}</td>
              <td style="border: 1px solid black !important;" class="align-middle text-uppercase font-weight-bold">{{ item.metaUnitName || item.unit || '-' }}</td>
              <td style="border: 1px solid black !important;" class="align-middle font-weight-bold">{{ formatNumber(item.qty) }}</td>
              <td style="border: 1px solid black !important;" class="align-middle font-weight-bold">{{ formatCurrency(item.rate) }}</td>
              <td style="border: 1px solid black !important;" class="align-middle font-weight-bold">{{ formatCurrency(item.valWithoutTax) }}</td>
              <td style="border: 1px solid black !important;" class="align-middle font-weight-bold">{{ formatNumber(item.salesTaxPercent) }}</td>
              <td style="border: 1px solid black !important;" class="align-middle font-weight-bold">{{ formatCurrency(item.salesTaxAmount) }}</td>
              <td style="border: 1px solid black !important;" class="align-middle font-weight-bold">{{ formatCurrency((item.valWithoutTax||0) + (item.salesTaxAmount||0)) }}</td>
            </tr>
          </tbody>
        </table>

        <!-- Bottom Summary / Payment Section -->
        <div class="invoice-summary-row mb-3">
          <!-- Amount in Words -->
          <div class="invoice-summary-left pt-1">
            <!-- SRO block if valid type and data exists -->
            <div v-if="shouldShowSroBlock(inv.invoiceData, inv.invoiceDetails)" class="mb-3" style="max-width: 300px;">
              <table class="w-100 text-left invoice-sro-table" style="font-size: 13px;">
                <thead style="background-color: #fff; border: 1px solid #000;">
                  <tr>
                    <th class="p-2 invoice-info-label" style="letter-spacing: 0.5px;">SRO<br>SCHEDULE<br>NO</th>
                    <th class="p-2 invoice-info-label" style="letter-spacing: 0.5px;">SRO<br>SERIAL<br>NO</th>
                  </tr>
                </thead>
                <tbody class="text-dark font-weight-bold">
                  <tr>
                    <td class="p-2 font-weight-bold" style="font-size: 14px;">{{ getSroScheduleNo(inv.invoiceData, inv.invoiceDetails) }}</td>
                    <td class="p-2 font-weight-bold" style="font-size: 14px;">{{ getSroItemSerialNo(inv.invoiceData, inv.invoiceDetails) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p class="text-dark font-weight-bold" style="font-size: 16px; margin-top: 1rem;">Amount In Word: <span class="text-uppercase">{{ numberToWords(getRoundedPrintTotalAmount(inv.invoiceData, inv.invoiceDetails)) }}</span></p>
          </div>

          <!-- Grand Totals Table -->
          <div class="ml-auto font-weight-bold invoice-totals-box">
            <div class="d-flex justify-content-between font-weight-bold invoice-total-row">
              <span class="text-uppercase font-weight-bold">EXCL TAX AMOUNT:</span>
              <span class="font-weight-bolder" style="font-size: 16px;">{{ formatCurrency(inv.invoiceData.valWithoutTax) }}</span>
            </div>
            <div class="d-flex justify-content-between font-weight-bold invoice-total-row">
              <span class="text-uppercase font-weight-bold">SALES TAX:</span>
              <span class="font-weight-bolder" style="font-size: 16px;">{{ formatCurrency(inv.invoiceData.totalSalesTax) }}</span>
            </div>
            <div class="d-flex justify-content-between font-weight-bold invoice-total-row">
              <span class="text-uppercase font-weight-bold">FURTHER TAX:</span>
              <span class="font-weight-bolder" style="font-size: 16px;">{{ formatCurrency(inv.invoiceData.totalFurtherTax) }}</span>
            </div>
            <div class="d-flex justify-content-between font-weight-bold invoice-total-row">
              <span style="font-weight: 900; font-size: 16px;">SUBTOTAL AMOUNT:</span>
              <span style="font-weight: 900; font-size: 16px;">{{ formatCurrency(getPrintSubtotalAmount(inv.invoiceData)) }}</span>
            </div>
            <div class="d-flex justify-content-between font-weight-bold" :class="isThirdSchedule(inv.invoiceData) ? 'invoice-total-row' : 'invoice-total-row invoice-total-row-strong'">
              <span style="font-weight: 900; font-size: 16px;">236G/H AMOUNT:</span>
              <span style="font-weight: 900; font-size: 16px;">{{ formatCurrency(getAdvanceTaxAmount(inv.invoiceData, inv.invoiceDetails)) }}</span>
            </div>
            <div v-if="isThirdSchedule(inv.invoiceData)" class="d-flex justify-content-between font-weight-bold invoice-total-row invoice-total-row-strong">
              <span style="font-weight: 900; font-size: 16px;">RETAIL VALUE:</span>
              <span style="font-weight: 900; font-size: 16px;">{{ formatCurrency(inv.totalRetailValue || 0) }}</span>
            </div>
            <div class="d-flex justify-content-between font-weight-bold" style="padding: 4px 0;">
              <span style="font-weight: 900; font-size: 18px;">TOTAL AMOUNT:</span>
              <span style="font-weight: 900; font-size: 19px;">{{ formatRoundedCurrency(getRoundedPrintTotalAmount(inv.invoiceData, inv.invoiceDetails)) }}</span>
            </div>
          </div>
        </div>

        <!-- FBR INTEGRATION SECTION -->
        <div v-if="inv.invoiceData.fbrInvoiceNo" class="fbr-section">
          <div class="fbr-invoice-banner text-center border border-dark">
            <div class="fbr-invoice-label">
              FBR DIGITAL INVOICE NO
            </div>
            <div class="fbr-invoice-number">
              {{ inv.invoiceData.fbrInvoiceNo }}
            </div>

            <div class="fbr-invoice-footer">
              <div class="fbr-visual-item">
                <div class="fbr-visual-box">
                  <img src="https://cdn.cloudious.net/file-1773737315176-755245077.png" alt="FBR Logo" class="fbr-logo-image" />
                </div>
                <div class="fbr-visual-caption">DIGITAL</div>
              </div>

              <div class="fbr-visual-item">
                <div class="fbr-visual-box">
                  <img
                    :src="`https://api.qrserver.com/v1/create-qr-code/?size=160x160&margin=0&data=${encodeURIComponent(inv.invoiceData.fbrInvoiceNo)}`"
                    alt="FBR QR"
                    class="fbr-qr-image"
                  />
                </div>
                <div class="fbr-visual-caption">SCAN QR</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer / Note -->
        <div class="text-center invoice-print-footer">
          <p class="text-uppercase invoice-footer-note">
            THIS IS SYSTEM GENERATED INVOICE NOT REQUIRED ANY STAMP OR SIGN
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton } from 'bootstrap-vue';
import { ref, onMounted, computed, onUnmounted } from '@vue/composition-api';
import store from '@/store';
import router from '@/router';
import { useToast } from 'vue-toastification/composition';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import moment from 'moment';

import invoiceStoreModule from '../invoiceStoreModule';
import CustomerStoreModule from '../../customer/CustomerStoreModule';
import BusinessStoreModule from '../../dashboard/business/BusinessStoreModule';

export default {
  components: {
    BButton,
  },
  setup() {
    const toast = useToast();
    const invoices = ref([]);
    const businessData = ref(null);
    const isLoading = ref(true);

    // Register modules locally to avoid undefined store states
    if (!store.hasModule('sales-invoice')) store.registerModule('sales-invoice', invoiceStoreModule);
    if (!store.hasModule('customer')) store.registerModule('customer', CustomerStoreModule);
    if (!store.hasModule('business')) store.registerModule('business', BusinessStoreModule);

    onMounted(async () => {
      try {
        const idParam = router.currentRoute.params.id;
        const ids = String(idParam).split(',');

        // Retrieve accurate Business Details
        try {
          const bizResponse = await store.dispatch('business/fetchBusinessDetails');
          if (bizResponse) {
             businessData.value = {
               name: bizResponse.name || bizResponse.businessName || 'Business Name',
               address: bizResponse.address_complete || bizResponse.address || bizResponse.city || '-',
               ntn: bizResponse.ntn || bizResponse.ntnNumber || '',
               cnic: bizResponse.cnic || bizResponse.cnicNumber || '',
               strn: bizResponse.strn || bizResponse.gstNumber || bizResponse.gstNo || '',
               metaData: {
                 ntn: bizResponse.ntn || bizResponse.ntnNumber || '',
                 cnic: bizResponse.cnic || bizResponse.cnicNumber || '',
                 strn: bizResponse.strn || bizResponse.gstNumber || bizResponse.gstNo || '',
               },
             };
          }
        } catch (e) {
          // Fallback if business API fails
          const userData = JSON.parse(localStorage.getItem('userData') || '{}');
          businessData.value = {
            name: store.state.user?.user?.name || userData?.name || userData?.metaBusinessName || userData?.fullName || 'Business Name',
            address: userData?.metaBusinessAddress || userData?.metaAddress || userData?.address || '-',
            ntn: userData?.metaNtn || userData?.ntn || userData?.ntnNumber || '',
            cnic: userData?.metaCnic || userData?.cnic || userData?.cnicNumber || '',
            strn: userData?.strn || userData?.metaStrn || userData?.gstNumber || userData?.gstNo || '',
            metaData: {
              ntn: userData?.metaNtn || userData?.ntn || userData?.ntnNumber || '',
              cnic: userData?.metaCnic || userData?.cnic || userData?.cnicNumber || '',
              strn: userData?.strn || userData?.metaStrn || userData?.gstNumber || userData?.gstNo || '',
            },
          };
        }

        // Fetch all invoices
        for (const singleId of ids) {
          if (!singleId) continue;
          try {
            const response = await store.dispatch('sales-invoice/fetchInvoice', { id: singleId });
            const invData = response.entity;
            const invDetails = response.details;

            let custData = {};
            try {
              custData = await store.dispatch('customer/fetchCustomer', { id: invData.customerID });
            } catch(e) {}

            invoices.value.push({
              invoiceData: invData,
              invoiceDetails: invDetails,
              customerData: custData,
              totalQty: invDetails.reduce((acc, curr) => acc + (parseFloat(curr.qty) || 0), 0),
              totalRetailValue: invDetails.reduce((acc, curr) => acc + (parseFloat(curr.notifiedValue || curr.fixedNotified || curr.fixedValue || curr.retailPrice) || 0), 0)
            });
          } catch(e) {
             console.error('Failed fetching single invoice', singleId);
          }
        }
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: { title: 'Error', variant: 'danger', text: 'Error loading print data' }
        });
      } finally {
        isLoading.value = false;
        // Optionally trigger print after render using small timeout
        // setTimeout(() => window.print(), 500); 
      }
    });

    const formatCurrency = (val) => Number(val || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const formatRoundedCurrency = (val) => Math.round(Number(val || 0)).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    const formatNumber = (val) => Number(val || 0).toLocaleString('en-US', { maximumFractionDigits: 2 });
    const formatDate = (date) => date ? moment(date).format('DD-MMM-YYYY') : '-';

    const isThirdSchedule = (invoiceData) => {
      const typeName = String(invoiceData?.metaSalesTypeName || invoiceData?.salesTypeName || invoiceData?.salesType || '');
      return typeName.toLowerCase().includes('3rd schedule');
    };

    const isSroSalesType = (invoiceData) => {
      const typeName = String(invoiceData?.metaSalesTypeName || invoiceData?.salesTypeName || invoiceData?.salesType || '').toLowerCase().trim();
      return typeName.includes('sro.297') || 
             typeName.includes('sro 297') ||
             typeName.includes('exempt') || 
             typeName.includes('zero-rate') || 
             typeName.includes('zero rate') ||
             typeName.includes('petroleum');
    };

    const isValidSroValue = (value) => {
      if (value === null || value === undefined) return false;

      const trimmed = String(value).trim();
      if (!trimmed) return false;

      const normalized = trimmed.toLowerCase();
      return normalized !== 'n/a' && normalized !== 'na' && normalized !== '-';
    };

    const getSroScheduleNo = (invoiceData, invoiceDetails) => {
      const value = invoiceData.sroScheduleNo
        || (invoiceDetails[0] && invoiceDetails[0].sroScheduleNo)
        || '';

      return isValidSroValue(value) ? String(value).trim() : '';
    };

    const getSroItemSerialNo = (invoiceData, invoiceDetails) => {
      const value = invoiceData.sroItemSerialNo
        || (invoiceDetails[0] && invoiceDetails[0].sroItemSerialNo)
        || '';

      return isValidSroValue(value) ? String(value).trim() : '';
    };

    const hasSroDisplayData = (invoiceData, invoiceDetails) => {
      return !!getSroScheduleNo(invoiceData, invoiceDetails)
        || !!getSroItemSerialNo(invoiceData, invoiceDetails);
    };

    const shouldShowSroBlock = (invoiceData, invoiceDetails) => {
      return isSroSalesType(invoiceData) && hasSroDisplayData(invoiceData, invoiceDetails);
    };

    const getFbrLink = (fbrNo) => fbrNo?.startsWith('http') ? fbrNo : `https://my.fbr.gov.pk/receipt/${fbrNo}`;

    const numberToWords = (amount) => {
      if (!amount || isNaN(amount)) return 'Zero';
      const a = ['', 'One ', 'Two ', 'Three ', 'Four ', 'Five ', 'Six ', 'Seven ', 'Eight ', 'Nine ', 'Ten ', 'Eleven ', 'Twelve ', 'Thirteen ', 'Fourteen ', 'Fifteen ', 'Sixteen ', 'Seventeen ', 'Eighteen ', 'Nineteen '];
      const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
      const inWords = (num) => {
        if ((num = num.toString()).length > 9) return 'overflow';
        let n = ('000000000' + num).substr(-9).match(/^(\d{2})(\d{2})(\d{2})(\d{1})(\d{2})$/);
        if (!n) return ''; 
        let str = '';
        str += (n[1] != 0) ? (a[Number(n[1])] || b[n[1][0]] + ' ' + a[n[1][1]]) + 'Crore ' : '';
        str += (n[2] != 0) ? (a[Number(n[2])] || b[n[2][0]] + ' ' + a[n[2][1]]) + 'Lakh ' : '';
        str += (n[3] != 0) ? (a[Number(n[3])] || b[n[3][0]] + ' ' + a[n[3][1]]) + 'Thousand ' : '';
        str += (n[4] != 0) ? (a[Number(n[4])] || b[n[4][0]] + ' ' + a[n[4][1]]) + 'Hundred ' : '';
        str += (n[5] != 0) ? ((str != '') ? 'and ' : '') + (a[Number(n[5])] || b[n[5][0]] + ' ' + a[n[5][1]]) : '';
        return str.trim();
      };
      
      const wholePart = Math.floor(amount);
      const decimalPart = Math.round((amount - wholePart) * 100);
      
      let words = inWords(wholePart);
      if (words === 'overflow') return 'Overflow';
      
      let res = words ? words : 'Zero';
      
      if (decimalPart > 0) {
        let decimalWords = inWords(decimalPart);
        if (decimalWords) {
          res += ' and ' + decimalWords + ' Paisa';
        }
      }
      return res + ' Only';
    };

    const getBusinessNtn = (data) => {
      if (!data) return '';

      const ntn = data.ntn || (data.metaData && data.metaData.ntn) || data.ntnNumber || '';
      const ntnValue = ntn ? String(ntn).trim() : '';

      return ntnValue && ntnValue !== '-' ? ntnValue : '';
    };

    const getBusinessCnic = (data) => {
      if (!data) return '';

      const cnic = data.cnic || (data.metaData && data.metaData.cnic) || data.cnicNumber || '';
      const cnicValue = cnic ? String(cnic).trim() : '';

      return cnicValue && cnicValue !== '-' ? cnicValue : '';
    };

    const getBusinessNtnCnicLabel = (data) => {
      if (getBusinessNtn(data)) return 'NTN';
      if (getBusinessCnic(data)) return 'CNIC';
      return 'NTN';
    };

    const getBusinessNtnCnicValue = (data) => {
      return getBusinessNtn(data) || getBusinessCnic(data) || '-';
    };

    const getBusinessStrn = (data) => {
      if (!data) return '';

      const strn = data.strn || data.gstNumber || data.gstNo || (data.metaData && data.metaData.strn) || '';
      const strnValue = strn ? String(strn).trim() : '';

      return strnValue && strnValue !== '-' ? strnValue : '';
    };

    const getCustomerGstNo = (customerData) => {
      if (!customerData) return '';

      const gstNo = customerData.gstNumber || customerData.gstNo || '';
      return gstNo && String(gstNo).trim() ? String(gstNo).trim() : '';
    };

    const getAdvanceTaxAmount = (invoiceData, invoiceDetails) => {
      if (invoiceData.totalAdvanceTax) {
        return Number(invoiceData.totalAdvanceTax) || 0;
      }

      return invoiceDetails.reduce(
        (sum, item) => sum + ((((item.valWithoutTax || 0) + (item.totalTax || 0)) * (item.tax236GValue || 0)) / 100),
        0
      ) || 0;
    };

    const getPrintSubtotalAmount = (invoiceData) => {
      return (Number(invoiceData.valWithoutTax) || 0)
        + (Number(invoiceData.totalSalesTax) || 0)
        + (Number(invoiceData.totalFurtherTax) || 0);
    };

    const getPrintTotalAmount = (invoiceData, invoiceDetails) => {
      return getPrintSubtotalAmount(invoiceData) + getAdvanceTaxAmount(invoiceData, invoiceDetails);
    };

    const getRoundedPrintTotalAmount = (invoiceData, invoiceDetails) => {
      return Math.round(getPrintTotalAmount(invoiceData, invoiceDetails));
    };

    const handlePrint = () => {
      const originalTitle = document.title;
      document.title = ' ';

      window.print();

      setTimeout(() => {
        document.title = originalTitle;
      }, 500);
    };
    const handleCancel = () => {
      if (window.history.length > 1) { router.go(-1); } else { router.push({ name: 'apps-invoice-list' }); }
    };

    return {
      invoices, businessData, isLoading,
      formatDate, formatCurrency, formatRoundedCurrency, formatNumber, numberToWords, getFbrLink, getCustomerGstNo, getBusinessNtnCnicLabel, getBusinessNtnCnicValue, getBusinessStrn, isThirdSchedule, isSroSalesType, getSroScheduleNo, getSroItemSerialNo, shouldShowSroBlock, getPrintSubtotalAmount, getPrintTotalAmount, getRoundedPrintTotalAmount, getAdvanceTaxAmount,
      handlePrint, handleCancel
    };
  }
};
</script>

<style lang="scss">
.invoice-print-content {
  max-width: 210mm;
  width: 100%;
  margin: 0 auto;
  padding: 12px;
  background-color: #fff !important;
  font-weight: 900;
  color: #000 !important;
  box-sizing: border-box;

  *,
  td,
  th,
  span,
  p,
  div,
  label,
  strong,
  small {
    color: #000 !important;
    font-weight: 900 !important;
  }

  h2 {
    font-size: 28px;
    font-weight: 900 !important;
  }

  h3 {
    font-size: 22px;
    font-weight: 900 !important;
  }

  .table,
  .table td,
  .table th {
    color: #000 !important;
    border-color: #000 !important;
    font-weight: 900 !important;
  }
}

.invoice-print-header {
  margin-bottom: 10px;
}

.invoice-print-title {
  margin-bottom: 10px;
}

.invoice-info-row {
  display: flex;
  flex-direction: row;
  align-items: stretch;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 10px;
}

.invoice-summary-row {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}

.invoice-summary-left {
  flex: 1 1 50%;
  max-width: 50%;
  padding-top: 4px;
}

.invoice-info-box-customer {
  flex: 1 1 54%;
  max-width: 54%;
  box-sizing: border-box;
}

.invoice-info-box-meta {
  flex: 0 0 44%;
  max-width: 44%;
  box-sizing: border-box;
}

.invoice-info-table {
  font-size: 14px;
  table-layout: fixed;
  width: 100%;
}

.invoice-info-label {
  font-weight: 900 !important;
  color: #000 !important;
  width: 42%;
}

.invoice-info-value {
  font-weight: 900 !important;
  color: #000 !important;
}

.invoice-meta-table {
  font-size: 15px;
}

.invoice-meta-table .invoice-info-label {
  width: 52%;
  padding-right: 8px;
}

.invoice-meta-value {
  white-space: nowrap;
  font-weight: 900 !important;
}

.invoice-sro-table th,
.invoice-sro-table td {
  border: 1px solid #000 !important;
  color: #000 !important;
}

.invoice-footer-note {
  font-size: 14px;
  font-weight: 900;
  color: #000 !important;
  margin-bottom: 0;
}

.invoice-totals-box {
  width: 45%;
  flex: 0 0 45%;
  max-width: 45%;
  margin-left: auto;
  font-size: 15px;
  color: #000 !important;
}

.print-table {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  font-size: 12px;
  margin-bottom: 8px;
}

.print-table th,
.print-table td {
  word-wrap: break-word;
  overflow-wrap: break-word;
  font-weight: 900 !important;
  padding: 4px 3px;
}

.invoice-print-header p {
  font-size: 15px;
  margin-bottom: 2px;
}

.invoice-print-footer {
  margin-top: 10px;
  padding-top: 4px;
}

.invoice-total-row {
  padding: 3px 0;
  border-bottom: 1px solid #000;
  font-weight: 900 !important;
}

.invoice-total-row span {
  font-weight: 900 !important;
}

.invoice-total-row-strong {
  border-bottom: 2px solid #000;
}

.fbr-invoice-banner {
  width: 100%;
  background-color: #fff;
  padding: 12px 10px !important;
}

.fbr-invoice-label {
  font-size: 18px;
  font-weight: 900 !important;
  letter-spacing: 1px;
  color: #000 !important;
  text-transform: uppercase;
  margin-bottom: 8px !important;
}

.fbr-invoice-number {
  font-size: 22px;
  font-weight: 900 !important;
  line-height: 1.3;
  letter-spacing: 1px;
  word-break: break-word;
  padding: 0 8px;
  color: #000 !important;
  margin-bottom: 12px !important;
}

.fbr-invoice-footer {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: center;
  align-items: flex-end;
  gap: 48px;
  width: 100%;
}

.fbr-visual-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  width: 130px;
  flex: 0 0 auto;
}

.fbr-visual-box {
  width: 120px;
  height: 120px;
  border: 2px solid #000;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #fff;
  box-sizing: border-box;
}

.fbr-logo-image {
  max-width: 92%;
  max-height: 92%;
}

.fbr-qr-image {
  width: 108px;
  height: 108px;
  display: block;
}

.fbr-visual-caption {
  min-height: 20px;
  line-height: 20px;
  margin-top: 6px;
  font-size: 13px;
  font-weight: 900 !important;
  letter-spacing: 0.5px;
  color: #000 !important;
  text-align: center;
}

.invoice-print-frame-body {
  margin: 0;
  padding: 10mm;
  background: #fff;
}

@media print {
  @page {
    size: A4 portrait;
    margin: 4mm;
  }

  html,
  body {
    background: #fff !important;
    margin: 0 !important;
    padding: 0 !important;
    width: 100% !important;
    height: auto !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  body * {
    visibility: hidden;
  }

  .invoice-print-wrapper,
  .invoice-print-wrapper * {
    visibility: visible;
    color: #000 !important;
    font-weight: 900 !important;
  }

  .invoice-print-wrapper {
    position: absolute;
    left: 0;
    top: 0;
    width: 100% !important;
    max-width: none !important;
    background-color: #fff !important;
    padding: 0 !important;
    margin: 0 !important;
    min-height: auto !important;
  }

  .print-hide,
  .main-menu,
  .header-navbar,
  .footer,
  .navbar,
  .content-header,
  .breadcrumb {
    display: none !important;
  }

  .app-content,
  .content-wrapper,
  .content-body {
    background: #fff !important;
    margin: 0 !important;
    padding: 0 !important;
    width: 100% !important;
    max-width: none !important;
  }

  .print-area,
  .invoice-print-content {
    width: 100% !important;
    max-width: none !important;
    margin: 0 !important;
    border: none !important;
    border-radius: 0 !important;
    padding: 0 !important;
    box-sizing: border-box;
  }

  .invoice-info-row,
  .invoice-summary-row {
    display: flex !important;
    flex-direction: row !important;
    flex-wrap: nowrap !important;
    width: 100% !important;
  }

  .fbr-invoice-footer {
    display: flex !important;
    flex-direction: row !important;
    flex-wrap: nowrap !important;
    align-items: flex-end !important;
    justify-content: center !important;
    width: 100% !important;
  }

  .invoice-info-box-customer {
    flex: 1 1 54% !important;
    max-width: 54% !important;
  }

  .invoice-info-box-meta {
    flex: 0 0 44% !important;
    max-width: 44% !important;
  }

  .invoice-totals-box {
    width: 45% !important;
    flex: 0 0 45% !important;
    max-width: 45% !important;
    margin-left: auto !important;
  }

  .print-table {
    width: 100% !important;
    table-layout: fixed !important;
    border-collapse: collapse !important;
    font-size: 11px !important;
  }

  .print-table th,
  .print-table td {
    font-weight: 900 !important;
    padding: 3px 2px !important;
  }

  .page-break {
    page-break-after: always;
    border: none !important;
  }

  .page-break:last-child {
    page-break-after: auto;
  }

  .fbr-section {
    margin-bottom: 8px !important;
    page-break-inside: avoid;
  }

  .fbr-invoice-banner {
    page-break-inside: avoid;
    padding: 10px 6px !important;
  }

  .fbr-invoice-footer {
    page-break-inside: avoid;
  }

  .invoice-footer-note {
    margin-top: 8px !important;
    padding-top: 4px !important;
  }
}
</style>
